import { pushDataLayer } from './utils';
import type { AnalyticsEvent, UserContextAttributes, EventWithDefaults } from './types';
import { getAuthentication, getSchoolContext } from '@/hooks/useAuthentication';
import { UsersPermissionsUser } from '@edwin-group/cj-graphql';

export const trackGAEvent = <T extends AnalyticsEvent>(
  event: T,
  data: EventWithDefaults<T> & Partial<UserContextAttributes>,
) => {
  pushDataLayer<EventWithDefaults<T>>(event, data);
};

const getCurrentUserId = () => getAuthentication()?.user?.documentId?.toString() || 'anonymous';

export const trackGAEventOnceExpiresIn5Minutes = <T extends AnalyticsEvent>(
  event: T,
  data: EventWithDefaults<T> & Partial<UserContextAttributes>,
  dedupeKeyParts: Array<string | number>,
) => {
  const userId = getCurrentUserId();
  const key = ['ga_once', userId, event, ...dedupeKeyParts].join(':');
  try {
    const now = Date.now();
    const ttlMs = 5 * 60 * 1000; // 5 minutes
    if (typeof window !== 'undefined') {
      const raw = window.sessionStorage?.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.timestamp === 'number' && now - parsed.timestamp < ttlMs) return;
      }
    }
    trackGAEvent(event, data);
    if (typeof window !== 'undefined') window.sessionStorage?.setItem(key, JSON.stringify({ timestamp: now }));
  } catch {
    trackGAEvent(event, data);
  }
};

export const getUserContext = (user: Omit<UsersPermissionsUser, 'school'> = getAuthentication()?.user) => {
  const context: UserContextAttributes = {
    user_document_id: '',
    user_name: '',
    user_role: '',
    school_document_id: '',
    school_name: '',
  };

  if (user) {
    const school = getSchoolContext();
    context.user_document_id = user?.documentId?.toString() || '';
    context.user_name = user?.forename + ' ' + user?.surname || '';
    context.user_role = user?.profile || '';
    context.school_document_id = school?.documentId || '';
    context.school_name = school?.name || '';
  }

  return context;
};
